import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.scss';

export interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom';
  delay?: number;
  className?: string;
}

export const Tooltip = ({
  text,
  children,
  position = 'top',
  delay = 100,
  className,
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const show = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setCoords({
          top: position === 'top' ? rect.top - 6 : rect.bottom + 6,
          left: rect.left + rect.width / 2,
        });
      }
      setVisible(true);
    }, delay);
  }, [delay, position]);

  const hide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
    setCoords(null);
  }, []);

  const updateCoords = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setCoords({
      top: position === 'top' ? rect.top : rect.bottom,
      left: rect.left + rect.width / 2,
    });
  }, [position]);

  useLayoutEffect(() => {
    if (visible) {
      updateCoords();
    }
  }, [visible, updateCoords]);

  if (!text) {
    return <>{children}</>;
  }

  return (
    <span
      ref={wrapperRef}
      className={`${styles.wrapper} ${className || ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {visible &&
        coords &&
        createPortal(
          <span
            className={`${styles.tooltip} ${styles[position]}`}
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {text}
            <span className={styles.arrow} />
          </span>,
          document.body
        )}
    </span>
  );
};
